import {Component, HostListener, OnInit} from '@angular/core';
import {Tenant} from "../../model/tenant";
import {Organization} from "../../model/organization";
import {CsvHistoryFilters} from "../../model/filters/csv-history-filters";
import {CsvHistory} from "../../model/csv-history";
import {SortOrder} from "../../model/sort-order";
import {FiltersService} from "../../services/filters.service";
import {OrganizationsService} from "../../services/organizations.service";
import {ConfirmationService, MessageService, SelectItem} from "primeng/api";
import {TranslocoService} from "@ngneat/transloco";
import {CommonMessagesService} from "../../services/common-messages.service";
import {TenantsService} from "../../services/tenants.service";
import {TimezoneService} from "../../services/timezone.service";
import {LoggedUser} from "../../common/logged-user.service";
import {AuthGuard} from "../../common/auth-guard.service";
import {PreviousRouteService} from "../../services/previous-route.service";
import {CsvHistoryService} from "../../services/csv-history.service";
import {saveAs} from 'file-saver';
import * as moment from "moment";

@Component({
  selector: 'app-csv-history',
  templateUrl: './csv-history.component.html',
  styleUrls: ['./csv-history.component.scss']
})
export class CsvHistoryComponent implements OnInit {

  public history: CsvHistory[];
  public totalFiles: number;
  public loadingHistory: boolean;

  public filtersVisible: boolean;
  public currentFilters: CsvHistoryFilters;

  public types: SelectItem[];
  public status: SelectItem[];
  public dateRanges: Date[];
  public tenants: Tenant[];
  public organizations: Organization[];
  public filtersFilled: boolean;

  public rows = 10;
  public first = 0;

  public userData: any;

  constructor(private csvHistoryService: CsvHistoryService,
              private organizationsService: OrganizationsService,
              private messageService: MessageService,
              private i18n: TranslocoService,
              private confirmationService: ConfirmationService,
              private commonMessagesService: CommonMessagesService,
              private tenantsService: TenantsService,
              private timezoneService: TimezoneService,
              public loggedUser: LoggedUser,
              public authGuardService: AuthGuard,
              private previousService: PreviousRouteService,
              private filtersService: FiltersService<CsvHistoryFilters>) {
    this.currentFilters = new CsvHistoryFilters();
    loggedUser.userObservable.subscribe(user => {
      this.userData = user;
    });
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    this.loadFilters().then(() => {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
      if (this.currentFilters?.hasOwnProperty('rangeDates')) {
        this.dateRanges = [new Date(this.currentFilters.rangeDates[0]), new Date(this.currentFilters.rangeDates[1])];
      }
    });
    this.history = new Array(this.rows);
  }

  private loadCsvHistory(offset: number, limit: number): void {
    this.loadingHistory = true;
    this.history = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.formatterDateForm(this.dateRanges);

    this.csvHistoryService.getCsvHistory(offset, limit, this.filtersService.getCurrentFilters(this.constructor.name) === undefined ? this.currentFilters : this.filtersService.getCurrentFilters(this.constructor.name))
      .subscribe(response => {
        this.history = response.csvHistory;
        this.totalFiles = response.totalElements;
        this.loadingHistory = false;
      });
  }

  private loadFilters(): Promise<any> {
    this.loadTypes();
    this.loadStatus();

    let promises: Array<Promise<any>> = [this.organizationsService.getOrganizations().toPromise()];

    if (this.authGuardService.superAdminRequired()) {
      promises.push(this.tenantsService.getTenants().toPromise());
    }
    return Promise.all(promises).then(response => {
      this.organizations = response[0].organizations.sort((a, b) => a.name > b.name ? 1 : -1);

      if (this.authGuardService.superAdminRequired()) {
        this.tenants = response[1].tenants.sort((a, b) => a.name > b.name ? 1 : -1);
      }
    });
  }

  private loadTypes(): void {
    this.i18n.selectTranslate('model.devices').subscribe(() => {
      this.types = [
        {label: this.i18n.translate('model.devices'), value: 'DEVICES'},
        {label: this.i18n.translate('model.users'), value: 'USERS'},
        {label: this.i18n.translate('model.measures'), value: 'MEASURES'}
      ];
    });
  }

  private loadStatus(): void {
    this.i18n.selectTranslate('info.importCsv.status.processing').subscribe(() => {
      this.status = [
        {label: this.i18n.translate('info.importCsv.status.processing'), value: 'PROCESSING'},
        {label: this.i18n.translate('info.importCsv.status.processed'), value: 'PROCESSED'},
        {label: this.i18n.translate('info.importCsv.status.error'), value: 'ERROR'}
      ];
    });
  }

  resetFilters(): void {
    this.currentFilters = new CsvHistoryFilters();
    this.currentFilters.sortField = 'uploadedDate';
    this.currentFilters.sortOrder = 0;
    this.dateRanges = null;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true) {
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadCsvHistory(this.first / this.rows, this.rows);
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadCsvHistory(event.first / event.rows, event.rows);
  }

  get filteredOrganizations(): Organization[] {
    if (this.authGuardService.superAdminRequired()) {
      if (this.currentFilters.tenantUuids?.length > 0) {
        return this.organizations?.filter(org => this.currentFilters.tenantUuids.includes(org.tenant.uuid));
      }
    }
    return this.organizations;
  }

  trimFieldsForm(filters: CsvHistoryFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  formatterDateForm(rangeDates: Date[]): void {
    if (rangeDates && rangeDates.length > 1) {
      if (!rangeDates[1]) {
        rangeDates[1] = moment.utc().toDate();
      }
      const utcRange = this.timezoneService.timestampRangeFromCalendarData(rangeDates);
      this.currentFilters.rangeDates = [utcRange.from, utcRange.until];
    }
  }

  downloadFile(csvHistory: CsvHistory) {
    const fileBase64 = csvHistory.fileBase64;

    const byteCharacters = atob(fileBase64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, {type: csvHistory.mimeTye});
    saveAs(blob, csvHistory.fileName);
  }


  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
